import React, { useRef, useEffect, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import "highcharts/modules/accessibility";
import "highcharts/highcharts-more";
import "highcharts/modules/solid-gauge";
import "./highcharts-responsive.css";
import { CardContent } from "@/components/ui/card";
import { useTenancy } from "@/contexts/TenancyContext";
import { deiterateAPI } from "@/js/external-js/variableModules/utils/deiterateAPI.util";
import { useDashboardWidgetContext } from "@/contexts/DashboardWidgetContext";

// Default values in case data is loading or there's an error
const DEFAULT_CONTROLS_IMPLEMENTED = 0;
const DEFAULT_TASKS_COMPLETED_ON_TIME = 0;
const MOCK_TASKS_ON_TIME = 54; // TODO: Replace with real data
const MOCK_USERS_COMPLETED_READING = 65; // TODO: Replace with real data
const RING_COLORS = ["#5B8DEF", "#00C49A", "#FFB547"];
const TRACK_COLORS = [
  "rgba(91,141,239,0.2)",
  "rgba(0,196,154,0.2)",
  "rgba(255,181,71,0.2)"
];

// Base chart configuration
const getChartOptions = (
  isEnterprise: boolean, 
  controlsImplemented: number, 
  tasksCompletedOnTime: number,
  usersCompletedReading: number,
  isStarter: boolean
): Highcharts.Options => {
  // Define panes based on enterprise status
  const panes = [
    {
      outerRadius: "112%",
      innerRadius: "88%",
      backgroundColor: TRACK_COLORS[0],
      borderWidth: 0,
    },
    {
      outerRadius: "87%",
      innerRadius: "63%",
      backgroundColor: TRACK_COLORS[1],
      borderWidth: 0,
    }
  ];

  // Add third pane only for non-enterprise
  if (!isEnterprise && !isStarter) {
    panes.push({
      outerRadius: "62%",
      innerRadius: "38%",
      backgroundColor: TRACK_COLORS[2],
      borderWidth: 0,
    });
  }

  // Define series based on enterprise status
  const series: Highcharts.SeriesSolidgaugeOptions[] = [
    {
      type: "solidgauge",
      name: "Controls Implemented",
      data: [
        {
          color: RING_COLORS[0],
          radius: "112%",
          innerRadius: "88%",
          y: controlsImplemented,
        },
      ],
    },
    {
      type: "solidgauge",
      name: "Tasks Completed On Time",
      data: [
        {
          color: RING_COLORS[1],
          radius: "87%",
          innerRadius: "63%",
          y: tasksCompletedOnTime,
        },
      ],
    },
  ];

  // Add third series only for non-enterprise
  if (!isEnterprise && !isStarter) {
    series.push({
      type: 'solidgauge',
      name: 'Users Completed Reading',
      data: [{
        color: RING_COLORS[2],
        radius: '62%',
        innerRadius: '38%',
        y: usersCompletedReading,
      }],
      dataLabels: {
        format: '{y}%',
        borderWidth: 0,
        style: {
          fontSize: '16px',
          fontWeight: 'bold',
          color: '#1F2937',
        },
        y: 0,
      },
      tooltip: {
        valueSuffix: '%',
        pointFormat: '<span style="color:{point.color}">●</span> {series.name}: <b>{point.y:.1f}%</b><br/>'
      }
    });
  }

  return {
    chart: {
      type: "solidgauge",
      height: 215,
      width: 315,
      backgroundColor: "transparent",
    },
    exporting: {
      enabled: false,
    },
    credits: {
      enabled: false
    },
    title: { text: undefined },
    tooltip: {
      borderWidth: 0,
      backgroundColor: "white",
      shadow: true,
      valueSuffix: "%",
      pointFormat:
        '<center>{series.name}<br>' +
        '<span style="font-size: 1em; color: {point.color}; font-weight: bold">{point.y}</span></center>',
      positioner: function (labelWidth) {
        // @ts-ignore
        return {
          x: (this.chart.chartWidth - labelWidth) / 2,
          y: this.chart.plotHeight / 2 + 15,
        };
      },
    },
    pane: {
      startAngle: 0,
      endAngle: 360,
      background: panes,
    },
    yAxis: {
      min: 0,
      max: 100,
      lineWidth: 0,
      tickPositions: [],
    },
    plotOptions: {
      solidgauge: {
        dataLabels: { enabled: false },
        linecap: "round",
        stickyTracking: false,
        rounded: true,
      },
    },
    series: series as Highcharts.SeriesOptionsType[],
  };
};

const WIDGET_ID = 'compliance-health';

const ComplianceHealthWidget: React.FC = () => {
  const { activeTenancy } = useTenancy();
  const { reportWidgetStatus } = useDashboardWidgetContext();
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [controlsImplemented, setControlsImplemented] = useState(DEFAULT_CONTROLS_IMPLEMENTED);
  const [tasksCompletedOnTime, setTasksCompletedOnTime] = useState(DEFAULT_TASKS_COMPLETED_ON_TIME);
  const [usersCompletedReading, setUsersCompletedReading] = useState(0);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);

  const isEnterprise = activeTenancy?.plan === "enterprise" || activeTenancy?.plan === "Enterprise Plan";
  const isStarter = activeTenancy?.plan === "starter" || activeTenancy?.plan === "Starter";
  const options = getChartOptions(isEnterprise, controlsImplemented, tasksCompletedOnTime, usersCompletedReading, isStarter);

  // Report widget status to dashboard context
  useEffect(() => {
    if (isLoading) {
      reportWidgetStatus(WIDGET_ID, 'loading');
    } else if (error) {
      reportWidgetStatus(WIDGET_ID, 'error');
    } else {
      reportWidgetStatus(WIDGET_ID, 'success');
    }
  }, [isLoading, error, reportWidgetStatus]);

  // Helper to handle API response and update state
  const handleApiResponse = (response: any) => {
    // Extract the widget data from the response
    const widgetData = response.data?.['compliance-health']?.data?.data;
    const metrics = widgetData?.metrics;
    const metadata = response.metadata;
    
    if (metrics) {
      // Update state with the fetched data
      setControlsImplemented(metrics.controlsImplemented ?? DEFAULT_CONTROLS_IMPLEMENTED);
      setTasksCompletedOnTime(metrics.tasksCompletedOnTime ?? DEFAULT_TASKS_COMPLETED_ON_TIME);
      setUsersCompletedReading(metrics.usersCompletedReading ?? 0);
      
      if (widgetData.lastUpdated) {
        setLastUpdated(new Date(widgetData.lastUpdated).toLocaleTimeString());
      } else if (metadata?.lastUpdated) {
        setLastUpdated(new Date(metadata.lastUpdated).toLocaleTimeString());
      }
      
      return true;
    }
    
    return false;
  };

  // State to track if data collection has been triggered
  const [collectionTriggered, setCollectionTriggered] = useState(false);
  
  // Fetch all data on component mount or when dependencies change
  useEffect(() => {
    if (!activeTenancy.id || activeTenancy.id === "" || activeTenancy.id === "Loading...") {
      return;
    }
    
    const fetchDashboardData = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Fetch data from the backend API
        const response = await deiterateAPI.GET(`/dashboard/data?customerId=${activeTenancy.id}&widgetType=compliance-health`);
        
        // Handle the API response and update state
        const success = handleApiResponse(response);
        
        if (!success) {
          console.warn('No compliance health data found in response');
          setError('No data available');
        } else {
          // If we previously triggered collection and now have data, stop polling
          if (collectionTriggered) {
            setCollectionTriggered(false);
          }
        }
      } catch (err: any) {
        console.error('Error fetching dashboard data:', err);
        
        // Check if this is a 404 response (data not found)
        if (err.response?.status === 404) {
          setCollectionTriggered(err.response.data?.collectionTriggered || false);
          setError(err.response.data?.message || 'Data not found. Please try again later.');
        } else {
          setError('Failed to load compliance health data');
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    // Set up a polling mechanism to check for data every 30 seconds
    const pollInterval = 30000; // 30 seconds
    let pollTimer: NodeJS.Timeout;
    
    // Initial fetch
    fetchDashboardData();
    
    // Set up polling if collection has been triggered for this tenancy
    if (collectionTriggered) {
      pollTimer = setInterval(() => {
        console.log('Polling for updated dashboard data...');
        fetchDashboardData();
      }, pollInterval);
    }
    
    // Clean up interval on component unmount
    return () => {
      if (pollTimer) clearInterval(pollTimer);
    };
  }, [activeTenancy.id, collectionTriggered]);

  // Handle chart resizing
  useEffect(() => {
    if (!containerRef.current || !chartRef.current) return;
    const node = containerRef.current;
    const resizeObserver = new window.ResizeObserver(() => {
      if (chartRef.current?.chart?.reflow) {
        chartRef.current.chart.reflow();
        setTimeout(() => {
          chartRef.current?.chart?.reflow();
        }, 100);
      }
    });
    resizeObserver.observe(node);
    return () => resizeObserver.disconnect();
  }, []);

  // Show loading state
  if (isLoading) {
    return (
      <CardContent className="h-full w-full p-6 flex flex-col items-center justify-center">
        <div className="animate-pulse flex flex-col items-center">
          <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded mb-4"></div>
          <div className="h-40 w-40 rounded-full bg-gray-200 dark:bg-gray-700"></div>
        </div>
      </CardContent>
    );
  }

  // Show error state or collection in progress
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-4">
        <div className="text-center p-6 bg-white rounded-lg shadow-sm border border-gray-200 max-w-md w-full">
          {collectionTriggered ? (
            <>
              <div className="text-blue-500 mb-4">
                <svg className="animate-spin h-12 w-12 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Data Collection in Progress</h3>
              <p className="text-gray-600 mb-4">
                We're gathering your compliance health data. This may take a few minutes.
              </p>
              <div className="bg-blue-50 text-blue-700 p-3 rounded-md text-sm">
                <p>Please check back in a few minutes. The page will refresh automatically.</p>
              </div>
            </>
          ) : (
            <>
              <div className="text-red-500 mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Data</h3>
              <p className="text-gray-600 mb-4">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                Retry
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <CardContent className="h-full w-full p-0 flex flex-col items-center justify-center">
      <div className="w-full flex justify-between items-center px-6 pt-4">
        <h3 className="text-lg font-semibold">Compliance Health</h3>
        {lastUpdated && (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            Updated: {lastUpdated}
          </span>
        )}
      </div>
      <div ref={containerRef} className="w-full h-full flex items-center justify-center">
        <HighchartsReact 
          highcharts={Highcharts} 
          options={options} 
          ref={chartRef} 
          containerProps={{ 
            style: { 
              width: '100%', 
              height: '100%',
              transition: 'opacity 300ms ease-in-out',
              opacity: isLoading ? 0.5 : 1
            } 
          }} 
        />
      </div>
    </CardContent>
  );
};

export default ComplianceHealthWidget;
